import { CoreApi, Dimension, Product } from '@inter-ikea-storageone/range';
import { ProductType } from '@inter-ikea-storageone/range';
import { FilterType } from 'rangeConfig/@types';
import * as R from 'ramda';
import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';

export const getNewFilterState = (type: FilterType, size: number, filterState: any) => {
  if (type === FilterType.FILTER) {
    return {
      ...filterState,
      [size]: !filterState[size]
    };
  }
  return { selected: size };
};

export const getFilteredProducts = (
  type: FilterType,
  size: number,
  dimension: Dimension,
  products: Product[],
  allProducts: Product[],
  filterState: any
) => {
  if (type === FilterType.FILTER) {
    return products.filter(p =>
      Object.keys(filterState)
        .filter(key => filterState[key])
        .includes(p.communicatedDimensions[dimension].toString())
    );
  }
  return products.filter(p => {
    if (p.type === ProductType.PAX_CORNER_SOLUTION_ASM) {
      const regularFrame = p.parts.find(part => part.product?.type === ProductType.PAX_FRAME)!
        .product!;
      const cornerFrame = p.parts.find(
        part => part.product?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM
      )!.product!;

      return (
        regularFrame.communicatedDimensions[dimension] === size &&
        cornerFrame.communicatedDimensions[dimension] === size
      );
    }
    return p.communicatedDimensions[dimension] === size;
  });
};

export const isFilterApplied = (type: FilterType, filterState: any) => {
  if (type === FilterType.FILTER) {
    return Object.values(filterState).some(f => f);
  }
  return true;
};

export const getFilterParams = (
  type: FilterType,
  size: number,
  dimension: Dimension,
  products: Product[],
  allProducts: Product[],
  filterState: any
) => {
  const newFilterState = getNewFilterState(type, size, filterState);
  const filterApplied = isFilterApplied(type, newFilterState);
  let filteredProducts = products;
  if (filterApplied) {
    filteredProducts = getFilteredProducts(
      type,
      size,
      dimension,
      products,
      allProducts,
      newFilterState
    );
  }
  return {
    productIds: filteredProducts.map(p => p.coreProductId),
    newFilterState
  };
};

export type FilterOption = {
  [dimension: number]: string;
};

export const constructFilterOptions = (dimension: Dimension) =>
  R.reduce((acc, r: Product) => {
    return {
      ...acc,
      [r.communicatedDimensions[dimension]]: r.communicatedDimensionsLocalized[dimension]
    };
  }, {});

export const getFilterOptions = (dimension: Dimension) =>
  R.pipe<Product[], FilterOption>(constructFilterOptions(dimension));

export const getUnitFromCommunicatedDimensions = R.reduce((acc: string, curr: Product) => {
  if (acc !== '' && curr.communicatedDimensionsLocalized.unit !== acc) {
    // eslint-disable-next-line no-console
    console.error('Unit mismatch in product data', acc, curr);
  }
  return curr.communicatedDimensionsLocalized.unit;
}, '');

export const getUnit = R.pipe<Product[], string>(getUnitFromCommunicatedDimensions);

export const filterHasCorrectOptionsLength = (dimension: string, products: Product[]) => {
  switch (dimension) {
    case 'width':
      const widthOptions = getFilterOptions(dimension)(products);
      return Object.keys(widthOptions).length > 1;
    case 'depth':
      const depthOptions = getFilterOptions(dimension)(
        products.filter(p => p.type !== ProductType.PAX_CORNER_SOLUTION_ASM)
      );
      return Object.keys(depthOptions).length > 1;
    case 'height':
      const heightOptions = getFilterOptions(dimension)(products);
      return Object.keys(heightOptions).length > 1;
    default:
      return true;
  }
};

export const isSizeFilter = (dimension: string) => {
  return dimension === 'width' || dimension === 'depth' || dimension === 'height';
};

export const findMaxFrameSizeInScene = (coreApi: CoreApi, dimension: Dimension): number | null =>
  coreApi.entities
    .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
    .reduce((maxSize: number | null, frame: Entity): number | null => {
      const frameSize = frame.getComponent<CommunicationSizeComponent>(
        ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
      )!.size[dimension];
      return maxSize === null || frameSize > maxSize ? frameSize : maxSize;
    }, null);
